
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import TextArea from '@/components/shared/TextArea.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import LockingButton from '@/components/shared/LockingButton.vue'
import { Note } from '@/@types/responses/note/Note'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { CustomerService } from '@/services/customer.service'
import { OrderService } from '@/services/order.service'
import { PurchaseOrderService } from '@/services/purchaseOrder.service'
import { QuoteService } from '@/services/quote.service'
import { OrderPartService } from '@/services/orderPart.service'
import { OrderServiceService } from '@/services/orderService.service'
import { QuotePartService } from '@/services/quotePart.service'
import { QuoteServiceService } from '@/services/quoteService.service'
import { NoteService } from '@/services/note.service'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'NoteModal',
  components: {
    Checkbox,
    CloseSvg,
    LockingButton,
    TextArea
  },
  props: {
    note: {
      type: Note,
      required: true
    },
    customerId: {
      type: Number,
      default: undefined
    },
    orderId: {
      type: Number,
      default: undefined
    },
    orderPartId: {
      type: Number,
      default: undefined
    },
    orderServiceId: {
      type: Number,
      default: undefined
    },
    purchaseOrderId: {
      type: Number,
      default: undefined
    },
    quoteId: {
      type: Number,
      default: undefined
    },
    quotePartId: {
      type: Number,
      default: undefined
    },
    quoteServiceId: {
      type: Number,
      default: undefined
    },
    showCustomerVisibleOption: {
      type: Boolean,
      delfault: false
    }
  },
  emits: ['note-changed', 'close'],
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const orderPartService = inject(PROVIDER_CONSTANTS.orderPartService) as OrderPartService
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const orderServiceService = inject(PROVIDER_CONSTANTS.orderServiceService) as OrderServiceService
    const purchaseOrderService = inject(PROVIDER_CONSTANTS.purchaseOrderService) as PurchaseOrderService
    const quotePartService = inject(PROVIDER_CONSTANTS.quotePartService) as QuotePartService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const quoteServiceService = inject(PROVIDER_CONSTANTS.quoteServiceService) as QuoteServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      customerService,
      noteService,
      orderPartService,
      orderService,
      orderServiceService,
      purchaseOrderService,
      quotePartService,
      quoteService,
      quoteServiceService,
      toast
    }
  },
  data() {
    return {
      content: this.note.content,
      customerVisible: this.showCustomerVisibleOption ? this.note.customerVisible : false,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.content !== this.note.content || this.customerVisible !== this.note.customerVisible) && this.content.trim() !== ''
    },
    titleText(): string {
      return this.note.id === 0 ? 'Create Note' : 'Edit Note'
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onNoteCreationFailure(error: any): void {
      this.saving = false

      if (error instanceof ErrorsResponse) {
        const errorsResponse = error as ErrorsResponse

        if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

        this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
      } else {
        this.toast.error('Failed to create the note. Please try again.')
      }
    },
    onNoteCreationSuccess(): void {
      this.$emit('note-changed')
      this.toast.success('Note created successfully.')
      this.$emit('close')
    },
    onSaveClicked(): void {
      this.saving = true

      const noteMessage = new NoteMessage(this.content, this.customerVisible)
      if (this.note.id === 0) {
        if (this.customerId !== undefined)
          this.customerService.createCustomerNote(this.customerId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.orderId !== undefined)
          this.orderService.createOrderNote(this.orderId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.orderPartId !== undefined)
          this.orderPartService.createOrderPartNote(this.orderPartId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.orderServiceId !== undefined)
          this.orderServiceService.createOrderServiceNote(this.orderServiceId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.purchaseOrderId !== undefined)
          this.purchaseOrderService.createPurchaseOrderNote(this.purchaseOrderId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.quoteId !== undefined)
          this.quoteService.createQuoteNote(this.quoteId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.quotePartId !== undefined)
          this.quotePartService.createQuotePartNote(this.quotePartId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
        else if (this.quoteServiceId !== undefined)
          this.quoteServiceService.createQuoteServiceNote(this.quoteServiceId, noteMessage).then(() => this.onNoteCreationSuccess()).catch(error => this.onNoteCreationFailure(error))
      } else 
        this.noteService.updateNote(this.note.id, noteMessage).then(() => {
          this.$emit('note-changed')
          this.toast.success('Note updated successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the note. Please try again.')
          }
        })
    }
  }
})
